<script>
    import Icon from '../icons/Icon.svelte';
    import { getNicheTemplate } from '../../data/nicheTemplates.js';

    export let stores = [];
    export let isLoading = false;
    export let searchQuery = '';
    export let statusFilter = 'all';
    export let onOpenCreateModal = () => {};
    export let onToggleBlockStore = () => {};
    export let onOpenDeleteModal = () => {};
    export let onSavePrice = () => {};
    export let onCopyWelcome = () => {};
    export let onCopyText = () => {};

    let editingPriceStoreId = null;
    let tempMonthlyPrice = '';

    $: filteredStores = stores.filter(store => {
        const matchesSearch = !searchQuery.trim() || 
            (store.name && store.name.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (store.email && store.email.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (store.slug && store.slug.toLowerCase().includes(searchQuery.toLowerCase())) ||
            (store.access_key && store.access_key.toLowerCase().includes(searchQuery.toLowerCase()));

        const matchesStatus = statusFilter === 'all' ||
            (statusFilter === 'active' && store.subscription_status === 'active') ||
            (statusFilter === 'blocked' && store.subscription_status === 'blocked');

        return matchesSearch && matchesStatus;
    });

    function startEditPrice(store) {
        editingPriceStoreId = store.id;
        tempMonthlyPrice = String(store.monthly_price || '97.00');
    }

    function cancelEditPrice() {
        editingPriceStoreId = null;
        tempMonthlyPrice = '';
    }

    function submitPrice(store) {
        onSavePrice(store, tempMonthlyPrice);
        editingPriceStoreId = null;
    }
</script>

<div class="stores-container">
    <!-- Barra Superior: Busca e Filtros -->
    <div class="table-toolbar">
        <div class="toolbar-search">
            <div class="search-input-box">
                <Icon name="search" size={15} />
                <input
                    type="text"
                    bind:value={searchQuery}
                    placeholder="Buscar por restaurante, e-mail, link ou chave..."
                    class="search-input"
                />
                {#if searchQuery}
                    <button type="button" class="btn-clear-search" on:click={() => searchQuery = ''}>
                        <Icon name="x" size={12} />
                    </button>
                {/if}
            </div>

            <div class="filter-pills">
                <button
                    type="button"
                    class="filter-pill {statusFilter === 'all' ? 'active' : ''}"
                    on:click={() => statusFilter = 'all'}
                >
                    Todas ({stores.length})
                </button>
                <button
                    type="button"
                    class="filter-pill {statusFilter === 'active' ? 'active' : ''}"
                    on:click={() => statusFilter = 'active'}
                >
                    Ativas ({stores.filter(s => s.subscription_status === 'active').length})
                </button>
                <button
                    type="button"
                    class="filter-pill {statusFilter === 'blocked' ? 'active' : ''}"
                    on:click={() => statusFilter = 'blocked'}
                >
                    Bloqueadas ({stores.filter(s => s.subscription_status === 'blocked').length})
                </button>
            </div>
        </div>
    </div>

    <!-- Tabela de Estabelecimentos -->
    {#if isLoading}
        <div class="loading-state">
            <div class="spinner"></div>
            <p>Carregando estabelecimentos e contratos...</p>
        </div>
    {:else if filteredStores.length === 0}
        <div class="empty-state">
            <Icon name="building" size={32} />
            <h4>Nenhum restaurante encontrado</h4>
            <p>{searchQuery ? 'Tente ajustar os termos de busca ou o filtro de status.' : 'Comece cadastrando seu primeiro cliente no botão acima.'}</p>
        </div>
    {:else}
        <div class="table-card">
            <div class="table-responsive">
                <table class="master-table">
                    <thead>
                        <tr>
                            <th>Estabelecimento & Ramo</th>
                            <th>Contato & Acesso</th>
                            <th>Atividade</th>
                            <th>Mensalidade</th>
                            <th>Status</th>
                            <th class="text-right">Ações Rápidas</th>
                        </tr>
                    </thead>
                    <tbody>
                        {#each filteredStores as store}
                            {@const niche = getNicheTemplate(store.business_profile)}
                            <tr class="table-row">
                                <!-- Coluna 1: Nome, Ramo e Onboarding -->
                                <td class="store-cell">
                                    <div class="store-identity">
                                        <div class="store-avatar" style="background: {niche.themeColor}22; color: {niche.themeColor};">
                                            <Icon name={niche.svgIcon || 'utensils'} size={18} />
                                        </div>
                                        <div class="store-info-text">
                                            <div class="name-row">
                                                <span class="store-name">{store.name}</span>
                                                <span class="niche-badge" style="background: {niche.themeColor}18; color: {niche.themeColor}; border: 1px solid {niche.themeColor}35;">
                                                    {niche.name}
                                                </span>
                                            </div>
                                            <div class="slug-row">
                                                <a
                                                    href="/cardapio/{store.slug}"
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    class="slug-link"
                                                    title="Abrir cardápio digital"
                                                >
                                                    /cardapio/{store.slug}
                                                    <Icon name="eye" size={11} />
                                                </a>
                                                <span class="onboarding-pill {store.onboarding_completed ? 'completed' : 'pending'}">
                                                    ● {store.onboarding_completed ? 'Onboarding Concluído' : 'Setup Pendente'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </td>

                                <!-- Coluna 2: Contato & Access Key -->
                                <td class="contact-cell">
                                    <div class="contact-box">
                                        <span class="contact-email">{store.email || '—'}</span>
                                        <span class="contact-phone">{store.phone || 'Sem telefone'}</span>
                                        <div class="access-key-row">
                                            <span class="key-badge">{store.access_key}</span>
                                            <button
                                                type="button"
                                                class="btn-copy-mini"
                                                title="Copiar chave de acesso"
                                                on:click={() => onCopyText(store.access_key, 'Chave de acesso copiada!')}
                                            >
                                                <Icon name="copy" size={12} />
                                            </button>
                                        </div>
                                    </div>
                                </td>

                                <!-- Coluna 3: Produtos & Pedidos -->
                                <td class="activity-cell">
                                    <div class="activity-badges">
                                        <span class="activity-tag">
                                            <strong>{store.total_products || 0}</strong> produtos
                                        </span>
                                        <span class="activity-tag">
                                            <strong>{store.total_orders || 0}</strong> pedidos
                                        </span>
                                    </div>
                                </td>

                                <!-- Coluna 4: Mensalidade (Edição inline) -->
                                <td class="price-cell">
                                    {#if editingPriceStoreId === store.id}
                                        <div class="inline-price-edit">
                                            <input
                                                type="number"
                                                step="1.00"
                                                bind:value={tempMonthlyPrice}
                                                class="input-inline-price"
                                            />
                                            <button type="button" class="btn-check-price" on:click={() => submitPrice(store)} title="Salvar">
                                                <Icon name="check" size={12} />
                                            </button>
                                            <button type="button" class="btn-cancel-price" on:click={cancelEditPrice} title="Cancelar">
                                                <Icon name="x" size={12} />
                                            </button>
                                        </div>
                                    {:else}
                                        <div class="price-display-box" on:click={() => startEditPrice(store)}>
                                            <span class="price-text">R$ {parseFloat(store.monthly_price || 97).toFixed(2)}</span>
                                            <span class="btn-edit-price-icon" title="Clique para editar valor">
                                                <Icon name="edit" size={11} />
                                            </span>
                                        </div>
                                    {/if}
                                </td>

                                <!-- Coluna 5: Status da Assinatura -->
                                <td class="status-cell">
                                    <button
                                        type="button"
                                        class="status-toggle-pill {store.subscription_status === 'active' ? 'active' : 'blocked'}"
                                        on:click={() => onToggleBlockStore(store)}
                                        title={store.subscription_status === 'active' ? 'Clique para bloquear acesso' : 'Clique para ativar acesso'}
                                    >
                                        <span class="status-dot"></span>
                                        <span>{store.subscription_status === 'active' ? 'Ativo' : 'Bloqueado'}</span>
                                    </button>
                                </td>

                                <!-- Coluna 6: Ações Rápidas -->
                                <td class="actions-cell text-right">
                                    <div class="actions-group">
                                        <button
                                            type="button"
                                            class="btn-action-icon whatsapp"
                                            title="Copiar mensagem de boas-vindas para o WhatsApp"
                                            on:click={() => onCopyWelcome(store)}
                                        >
                                            <Icon name="whatsapp" size={14} />
                                        </button>
                                        <a
                                            href="/cardapio/{store.slug}"
                                            target="_blank"
                                            rel="noreferrer"
                                            class="btn-action-icon"
                                            title="Abrir cardápio digital"
                                        >
                                            <Icon name="globe" size={14} />
                                        </a>
                                        <button
                                            type="button"
                                            class="btn-action-icon del"
                                            title="Excluir restaurante"
                                            on:click={() => onOpenDeleteModal(store)}
                                        >
                                            <Icon name="trash" size={14} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        {/each}
                    </tbody>
                </table>
            </div>
        </div>
    {/if}
</div>

<style>
    .stores-container {
        display: flex;
        flex-direction: column;
        gap: 16px;
    }

    .table-toolbar {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 16px;
        flex-wrap: wrap;
    }

    .toolbar-search {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        flex-wrap: wrap;
    }

    .search-input-box {
        position: relative;
        display: flex;
        align-items: center;
        background: #FFFFFF;
        border: 1px solid #CBD5E1;
        border-radius: 8px;
        padding: 0 12px;
        min-width: 320px;
        color: #64748B;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.03);
        transition: border-color 0.2s, box-shadow 0.2s;
    }

    .search-input-box:focus-within {
        border-color: #FF6A00;
        box-shadow: 0 0 0 2px rgba(255, 106, 0, 0.15);
    }

    .search-input {
        background: transparent;
        border: none;
        padding: 9px 8px;
        color: #0F172A;
        font-size: 13px;
        width: 100%;
    }

    .search-input:focus {
        outline: none;
    }

    .btn-clear-search {
        background: none;
        border: none;
        color: #94A3B8;
        cursor: pointer;
        padding: 2px;
    }

    .btn-clear-search:hover {
        color: #0F172A;
    }

    .filter-pills {
        display: flex;
        align-items: center;
        gap: 4px;
        background: #FFFFFF;
        padding: 3px;
        border-radius: 8px;
        border: 1px solid #E2E8F0;
    }

    .filter-pill {
        background: transparent;
        border: none;
        color: #64748B;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .filter-pill:hover {
        color: #0F172A;
        background: #F8FAFC;
    }

    .filter-pill.active {
        background: #0F172A;
        color: #FFFFFF;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .table-card {
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 12px;
        overflow: hidden;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
    }

    .table-responsive {
        overflow-x: auto;
    }

    .master-table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
        text-align: left;
    }

    .master-table th {
        background: #F8FAFC;
        padding: 12px 16px;
        color: #475569;
        font-size: 11px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        border-bottom: 1px solid #E2E8F0;
    }

    .master-table td {
        padding: 14px 16px;
        border-bottom: 1px solid #F1F5F9;
        vertical-align: middle;
        color: #0F172A;
    }

    .table-row:hover {
        background: #F8FAFC;
    }

    .store-identity {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .store-avatar {
        width: 38px;
        height: 38px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
    }

    .store-info-text {
        display: flex;
        flex-direction: column;
        gap: 3px;
    }

    .name-row {
        display: flex;
        align-items: center;
        gap: 8px;
    }

    .store-name {
        font-size: 14px;
        font-weight: 700;
        color: #0F172A;
    }

    .niche-badge {
        font-size: 11px;
        font-weight: 700;
        padding: 2px 7px;
        border-radius: 6px;
    }

    .slug-row {
        display: flex;
        align-items: center;
        gap: 10px;
    }

    .slug-link {
        font-family: monospace;
        font-size: 12px;
        color: #64748B;
        text-decoration: none;
        display: inline-flex;
        align-items: center;
        gap: 4px;
    }

    .slug-link:hover {
        color: #FF6A00;
    }

    .onboarding-pill {
        font-size: 11px;
        font-weight: 600;
    }

    .onboarding-pill.completed {
        color: #16A34A;
    }

    .onboarding-pill.pending {
        color: #D97706;
    }

    .contact-box {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .contact-email {
        font-size: 13px;
        color: #0F172A;
        font-weight: 600;
    }

    .contact-phone {
        font-size: 12px;
        color: #64748B;
    }

    .access-key-row {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        margin-top: 2px;
    }

    .key-badge {
        font-family: monospace;
        font-size: 11px;
        background: #F1F5F9;
        color: #475569;
        border: 1px solid #E2E8F0;
        padding: 2px 6px;
        border-radius: 4px;
    }

    .btn-copy-mini {
        background: none;
        border: none;
        color: #94A3B8;
        cursor: pointer;
        padding: 2px;
        display: flex;
    }

    .btn-copy-mini:hover {
        color: #FF6A00;
    }

    .activity-badges {
        display: flex;
        flex-direction: column;
        gap: 4px;
    }

    .activity-tag {
        font-size: 12px;
        color: #64748B;
    }

    .activity-tag strong {
        color: #0F172A;
    }

    .price-display-box {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        cursor: pointer;
        padding: 4px 8px;
        border-radius: 6px;
        transition: background 0.15s ease;
    }

    .price-display-box:hover {
        background: #F1F5F9;
    }

    .price-text {
        font-weight: 700;
        color: #16A34A;
        font-size: 14px;
    }

    .btn-edit-price-icon {
        color: #94A3B8;
    }

    .price-display-box:hover .btn-edit-price-icon {
        color: #FF6A00;
    }

    .inline-price-edit {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    .input-inline-price {
        width: 80px;
        background: #FFFFFF;
        border: 1px solid #FF6A00;
        border-radius: 6px;
        padding: 4px 6px;
        color: #0F172A;
        font-size: 13px;
        font-weight: 600;
    }

    .btn-check-price, .btn-cancel-price {
        background: #F1F5F9;
        border: 1px solid #CBD5E1;
        color: #475569;
        padding: 5px;
        border-radius: 4px;
        cursor: pointer;
        display: flex;
    }

    .btn-check-price:hover {
        background: #16A34A;
        border-color: #16A34A;
        color: #FFFFFF;
    }

    .btn-cancel-price:hover {
        background: #DC2626;
        border-color: #DC2626;
        color: #FFFFFF;
    }

    .status-toggle-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        padding: 4px 10px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.15s ease;
    }

    .status-toggle-pill.active {
        background: #DCFCE7;
        color: #166534;
        border: 1px solid #BBF7D0;
    }

    .status-toggle-pill.active:hover {
        background: #BBF7D0;
    }

    .status-toggle-pill.blocked {
        background: #FEE2E2;
        color: #991B1B;
        border: 1px solid #FECACA;
    }

    .status-toggle-pill.blocked:hover {
        background: #FECACA;
    }

    .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: currentColor;
    }

    .actions-group {
        display: inline-flex;
        align-items: center;
        gap: 6px;
    }

    .btn-action-icon {
        width: 32px;
        height: 32px;
        border-radius: 6px;
        background: #F8FAFC;
        border: 1px solid #E2E8F0;
        color: #64748B;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        cursor: pointer;
        text-decoration: none;
        transition: all 0.15s ease;
    }

    .btn-action-icon:hover {
        background: #F1F5F9;
        color: #0F172A;
        border-color: #CBD5E1;
    }

    .btn-action-icon.whatsapp:hover {
        background: #DCFCE7;
        border-color: #86EFAC;
        color: #166534;
    }

    .btn-action-icon.del:hover {
        background: #FEE2E2;
        border-color: #FCA5A5;
        color: #991B1B;
    }

    .text-right {
        text-align: right;
    }

    .loading-state, .empty-state {
        text-align: center;
        padding: 60px 20px;
        color: #64748B;
    }

    .empty-state h4 {
        color: #0F172A;
        margin: 10px 0 4px 0;
        font-size: 15px;
    }

    .spinner {
        width: 32px;
        height: 32px;
        border: 3px solid #E2E8F0;
        border-top-color: #FF6A00;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        margin: 0 auto 12px auto;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }
</style>
